import {
  BackComponent,
  FormCheckBox,
  FormInput,
  PageMainContent,
  Typography,
} from '@/components'
import { usePaymentMethodValidation } from '@/pages/EditPaymentPage/usePaymentMethodValidation'
import { FormProvider, useSnackbar } from '@/providers'
import { useCreatePayment } from '@/services'
import { useGoBack, useGoToPath, useHistoryState } from '@/utils'
import { Button } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PageActions, PageTitle } from './styles'

interface FormValues {
  name: string
  iban: string
  bic: string
  priority: 0 | 1
}

interface CreatePaymentInfo extends FormValues {
  companyId: string
}

const PageForm = () => {
  const { t } = useTranslation()
  const { companyId = '' } = useParams()
  const snackbar = useSnackbar()
  const { from } = useHistoryState()
  const validationSchema = usePaymentMethodValidation()

  const goBack = useGoBack()
  const goToFrom = useGoToPath(from, {}, { fromPayments: true })

  const onLeave = () => {
    if (from) goToFrom()
    else goBack()
  }

  const createPaymentMutation = useCreatePayment()

  const addNewPayment = (values: CreatePaymentInfo) =>
    createPaymentMutation
      .mutateAsync(values)
      .then(() => {
        snackbar.success(t('snackbars.success.addPayment'))
        onLeave()
      })
      .catch(() => snackbar.error(t('snackbars.error.general')))

  const handleSubmit = (values: FormValues) =>
    addNewPayment({
      iban: values.iban,
      bic: values.bic,
      name: values.name,
      priority: values.priority ? 1 : 0,
      companyId,
    })

  return (
    <FormProvider onSubmit={handleSubmit} validationSchema={validationSchema}>
      <FormInput label={t('profile.payments.name.label')} name="name" />
      <FormInput label={t('profile.payments.iban.label')} name="iban" />
      <PageActions>
        <FormCheckBox
          initialValue={false}
          label={t('profile.payments.isStandard')}
          name="priority"
        />
      </PageActions>
      <PageActions>
        <Button
          onClick={onLeave}
          disabled={createPaymentMutation.isLoading}
          variant="muted"
          shape="square"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          loading={createPaymentMutation.isLoading}
          variant="primary"
          shape="square"
        >
          {t('common.save')}
        </Button>
      </PageActions>
    </FormProvider>
  )
}
export const AddPaymentPage = () => {
  const { t } = useTranslation()
  return (
    <>
      <PageMainContent size="sm">
        <BackComponent />
        <PageTitle>
          <Typography variant="h4">{t('add_payment_details')}</Typography>
        </PageTitle>
        <PageForm />
      </PageMainContent>
    </>
  )
}
